<template>
    <RegularModal ref="modal">
        <template #title>{{ translate("page.tech_account.add_title") }}</template>
        <template #body>
            <p class="mb-3">
                {{ translate("page.tech_account.here_you_can_create") }}
            </p>
            <div :class="getFormGroupClass('username')">
                <label class="label">{{ translate("page.tech_account.username") }}:</label>
                <div class="control">
                    <input id="techAccountUsername" type="text" class="input" v-model="username" maxlength="45" />
                </div>
                <p class="help">
                    {{ getValidationMessage("username") }}
                </p>
            </div>
            <div :class="getFormGroupClass('password')">
                <label class="label">{{ translate("page.tech_account.password_visible") }}:</label>
                <div class="control">
                    <input type="text" class="input" v-model="password" id="techAccountPassword" />
                </div>
                <p class="help">
                    {{ getValidationMessage("password") }}
                </p>
            </div>
            <TechAccountAccessFields
                v-model:mode="mode"
                v-model:bundles="bundles"
                radio-name="techAccountMode-new"
                help-key="page.tech_account.access_mode_help_create"
            />
        </template>
        <template #footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="creatingInProgress" class="button is-primary" :disabled="!canCreate" @click="createTechAccount">
                    <span class="icon">
                        <i class="fas fa-user-plus"></i>
                    </span>
                    <span>{{ translate("common.ok") }}</span>
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import TechAccountAccessFields from "./TechAccountAccessFields.vue";
    import { TECH_ACCOUNT_MODE_BUNDLED, TECH_ACCOUNT_MODE_FULL_ACCESS, Bundle, TechAccountMode } from "../Common/consts";
    import { useModal } from "../Common/useModal";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { useValidationErrors } from "../Common/useValidationErrors";
    import techAccount, { TechAccount } from "../Api/techAccount";

    const props = defineProps<{ eventId: number }>();

    const emit = defineEmits<{
        // The plain password is only known here, right after creation.
        created: [account: TechAccount, plainPassword: string];
    }>();

    const { modal, open: openModal, close } = useModal();
    const { getFormGroupClass, getValidationMessage, clearValidationErrors, updateValidationErrors } = useValidationErrors();

    const username = ref("");
    const password = ref("");
    const mode = ref<TechAccountMode>(TECH_ACCOUNT_MODE_FULL_ACCESS);
    const bundles = ref<Bundle[]>([]);
    const creatingInProgress = ref(false);
    const canCreate = computed(() => mode.value !== TECH_ACCOUNT_MODE_BUNDLED || bundles.value.length > 0);

    function open(): void {
        username.value = "";
        password.value = "";
        mode.value = TECH_ACCOUNT_MODE_FULL_ACCESS;
        bundles.value = [];
        clearValidationErrors();
        openModal();
    }

    function createTechAccount() {
        creatingInProgress.value = true;
        const plainPassword = password.value;
        return techAccount
            .createTechAccount(props.eventId, username.value, plainPassword, mode.value, bundles.value)
            .then(account => {
                emit("created", account, plainPassword);
                close();
            })
            .catch(err => {
                updateValidationErrors(err.validationErrors);

                handleError(err);
            })
            .finally(() => {
                creatingInProgress.value = false;
            });
    }

    defineExpose({ open });
</script>
